import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserRole } from '@teffy/shared-types'
import { api } from '../services/api'
import { PageHeader, Card, Button, Modal, Field, Input, Select, Badge, EmptyState, ConfirmModal } from '../components/ui'

interface StaffMember {
  id: string
  name: string
  email: string
  role: UserRole
  canReserve: boolean
}

interface MenuDishSummary {
  id: string
  name: string
  category: string
}

interface MenuWithDishes {
  id: string
  name: string
  dishes: MenuDishSummary[]
}

const ROLE_LABEL: Record<string, string> = {
  [UserRole.WAITER]:    'Mesero',
  [UserRole.KITCHEN]:   'Cocina/Bar',
  [UserRole.RECEPTION]: 'Recepción',
  [UserRole.VALET]:     'Valet',
  [UserRole.HOSTESS]:   'Hostess',
  [UserRole.OWNER]:     'Dueño / Todo en uno',
}

const ROLE_COLOR: Record<string, 'blue' | 'purple' | 'green' | 'gray' | 'yellow' | 'teal'> = {
  [UserRole.WAITER]:    'blue',
  [UserRole.KITCHEN]:   'purple',
  [UserRole.RECEPTION]: 'green',
  [UserRole.VALET]:     'gray',
  [UserRole.HOSTESS]:   'teal',
  [UserRole.OWNER]:     'yellow',
}

export default function StaffPage() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', password: '', role: UserRole.WAITER })
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<StaffMember | null>(null)
  const [editTarget, setEditTarget] = useState<StaffMember | null>(null)
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', canReserve: false })
  const [editError, setEditError] = useState('')
  const [selectedDishIds, setSelectedDishIds] = useState<string[]>([])
  const [dishesInitialized, setDishesInitialized] = useState(false)

  const { data: staff = [], isLoading } = useQuery<StaffMember[]>({
    queryKey: ['staff'],
    queryFn: async () => (await api.get('/restaurant/staff')).data,
  })

  const { data: restaurant } = useQuery<{ venueType: string }>({
    queryKey: ['restaurant-settings'],
    queryFn: async () => (await api.get('/restaurant')).data,
    staleTime: 5 * 60 * 1000,
  })

  const { data: menuDishes = [], isLoading: isLoadingDishes } = useQuery<MenuWithDishes[]>({
    queryKey: ['restaurant-dishes'],
    queryFn: async () => (await api.get('/restaurant/dishes')).data,
    enabled: editTarget?.role === UserRole.KITCHEN,
    staleTime: 2 * 60 * 1000,
  })

  const { data: staffDishIds } = useQuery<string[]>({
    queryKey: ['staff-dishes', editTarget?.id],
    queryFn: async () => (await api.get(`/restaurant/staff/${editTarget!.id}/dishes`)).data,
    enabled: !!editTarget && editTarget.role === UserRole.KITCHEN,
  })

  useEffect(() => {
    if (staffDishIds && !dishesInitialized) {
      setSelectedDishIds(staffDishIds)
      setDishesInitialized(true)
    }
  }, [staffDishIds, dishesInitialized])

  const RECEPTION_VENUES = new Set(['HOTEL', 'APARTMENT', 'HOSPITAL'])
  const hasReception = RECEPTION_VENUES.has(restaurant?.venueType ?? '')

  const { mutate: create, isPending } = useMutation({
    mutationFn: async () => api.post('/auth/staff', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] })
      setOpen(false)
      setForm({ name: '', email: '', password: '', role: UserRole.WAITER })
      setError('')
    },
    onError: (err: unknown) => {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error al crear el empleado')
    },
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {}
      if (editForm.name && editForm.name !== editTarget?.name) payload.name = editForm.name
      if (editForm.email && editForm.email !== editTarget?.email) payload.email = editForm.email
      if (editForm.password) payload.password = editForm.password
      if (editTarget?.role === UserRole.WAITER) payload.canReserve = editForm.canReserve
      await api.patch(`/restaurant/staff/${editTarget!.id}`, payload)
      if (editTarget?.role === UserRole.KITCHEN) {
        await api.put(`/restaurant/staff/${editTarget!.id}/dishes`, { dishIds: selectedDishIds })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] })
      qc.invalidateQueries({ queryKey: ['staff-dishes', editTarget?.id] })
      setEditTarget(null)
      setEditError('')
    },
    onError: (err: unknown) => {
      setEditError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error al actualizar')
    },
  })

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => api.delete(`/restaurant/staff/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff'] }),
  })

  const owners = staff.filter((s) => s.role === UserRole.OWNER)
  const waiters = staff.filter((s) => s.role === UserRole.WAITER)
  const kitchen = staff.filter((s) => s.role === UserRole.KITCHEN)
  const reception = staff.filter((s) => s.role === UserRole.RECEPTION)
  const valets = staff.filter((s) => s.role === UserRole.VALET)
  const hostesses = staff.filter((s) => s.role === UserRole.HOSTESS)

  function handleClose() {
    setOpen(false)
    setError('')
    setForm({ name: '', email: '', password: '', role: UserRole.WAITER })
  }

  function openEdit(member: StaffMember) {
    setEditTarget(member)
    setEditForm({ name: member.name, email: member.email, password: '', canReserve: member.canReserve })
    setEditError('')
    setSelectedDishIds([])
    setDishesInitialized(false)
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Personal"
        subtitle={`${staff.filter(s => s.role !== UserRole.OWNER).length} empleado(s) · ${owners.length} administrador(es)`}
        action={<Button onClick={() => setOpen(true)}>+ Nuevo empleado</Button>}
      />

      {isLoading ? (
        <div className="text-sm text-gray-400">Cargando…</div>
      ) : staff.length === 0 ? (
        <EmptyState emoji="👤" title="Sin personal" sub="Agrega meseros y personal de cocina" />
      ) : (
        <>
        {/* Sección Administradores — separada del resto del personal */}
        {owners.length > 0 && (
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2 bg-amber-100">
                <span className="text-base font-bold text-amber-900">Administradores</span>
                <Badge label={String(owners.length)} color="yellow" />
                <span className="ml-auto text-xs text-amber-700">Acceso completo al panel</span>
              </div>
              <div className="divide-y divide-amber-100">
                {owners.map((s) => (
                  <div key={s.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                      <p className="text-xs text-amber-700">{s.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge label="Dueño / Todo en uno" color="yellow" />
                      <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>Editar</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 auto-rows-min">
          <Card>
            <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
              <h2 className="font-bold text-gray-900">Meseros</h2>
              <Badge label={String(waiters.length)} color="blue" />
            </div>
            {waiters.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">Sin meseros registrados</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {waiters.map((s) => (
                  <StaffRow key={s.id} member={s} onEdit={() => openEdit(s)} onRemove={() => setConfirmDelete(s)} />
                ))}
              </div>
            )}
          </Card>

          <Card>
            <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
              <h2 className="font-bold text-gray-900">Cocina/Bar</h2>
              <Badge label={String(kitchen.length)} color="purple" />
            </div>
            {kitchen.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">Sin personal de cocina/bar</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {kitchen.map((s) => (
                  <StaffRow key={s.id} member={s} onEdit={() => openEdit(s)} onRemove={() => setConfirmDelete(s)} />
                ))}
              </div>
            )}
          </Card>

          {hasReception && (
            <Card>
              <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                <h2 className="font-bold text-gray-900">Recepción</h2>
                <Badge label={String(reception.length)} color="green" />
              </div>
              {reception.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Sin personal de recepción</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {reception.map((s) => (
                    <StaffRow key={s.id} member={s} onEdit={() => openEdit(s)} onRemove={() => setConfirmDelete(s)} />
                  ))}
                </div>
              )}
            </Card>
          )}

          {valets.length > 0 && (
            <Card>
              <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                <h2 className="font-bold text-gray-900">Valet</h2>
                <Badge label={String(valets.length)} color="gray" />
              </div>
              <div className="divide-y divide-gray-50">
                {valets.map((s) => (
                  <StaffRow key={s.id} member={s} onEdit={() => openEdit(s)} onRemove={() => setConfirmDelete(s)} />
                ))}
              </div>
            </Card>
          )}
          {hostesses.length > 0 && (
            <Card>
              <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                <h2 className="font-bold text-gray-900">Hostess</h2>
                <Badge label={String(hostesses.length)} color="teal" />
              </div>
              <div className="divide-y divide-gray-50">
                {hostesses.map((s) => (
                  <StaffRow key={s.id} member={s} onEdit={() => openEdit(s)} onRemove={() => setConfirmDelete(s)} />
                ))}
              </div>
            </Card>
          )}
        </div>
        </>
      )}

      {/* Modal editar empleado */}
      <Modal
        title={`Editar — ${editTarget?.name}`}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
      >
        <div className="space-y-4">
          <Field label="Nombre">
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nombre completo"
            />
          </Field>
          <Field label="Correo electrónico">
            <Input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="correo@restaurante.com"
            />
          </Field>
          <Field label="Nueva contraseña (dejar vacío para no cambiar)">
            <Input
              type="password"
              value={editForm.password}
              onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
            />
          </Field>
          {editTarget?.role === UserRole.KITCHEN && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Platillos asignados</p>
              {isLoadingDishes ? (
                <p className="text-xs text-gray-400">Cargando platillos…</p>
              ) : menuDishes.length === 0 ? (
                <p className="text-xs text-gray-400">No hay platillos registrados en el menú</p>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                  {menuDishes.map((menu) => {
                    const allIds = menu.dishes.map((d) => d.id)
                    const allSelected = allIds.length > 0 && allIds.every((id) => selectedDishIds.includes(id))
                    return (
                      <div key={menu.id}>
                        <div className="px-3 py-2 bg-gray-50 flex items-center justify-between sticky top-0">
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{menu.name}</span>
                          {menu.dishes.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-blue-500 hover:text-blue-700"
                              onClick={() => {
                                if (allSelected) {
                                  setSelectedDishIds((prev) => prev.filter((id) => !allIds.includes(id)))
                                } else {
                                  setSelectedDishIds((prev) => [...new Set([...prev, ...allIds])])
                                }
                              }}
                            >
                              {allSelected ? 'Ninguno' : 'Todos'}
                            </button>
                          )}
                        </div>
                        {menu.dishes.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-gray-400">Sin platillos</p>
                        ) : menu.dishes.map((dish) => (
                          <label key={dish.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedDishIds.includes(dish.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDishIds((prev) => [...prev, dish.id])
                                } else {
                                  setSelectedDishIds((prev) => prev.filter((id) => id !== dish.id))
                                }
                              }}
                              className="accent-blue-500 w-4 h-4 shrink-0"
                            />
                            <span className="text-sm text-gray-800">{dish.name}</span>
                          </label>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                {selectedDishIds.length === 0
                  ? 'Sin platillos asignados — verá todos los pedidos de cocina'
                  : `${selectedDishIds.length} platillo(s) seleccionado(s)`}
              </p>
            </div>
          )}
          {editTarget?.role === UserRole.WAITER && (
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                className={`relative w-10 h-6 rounded-full transition-colors ${editForm.canReserve ? 'bg-blue-500' : 'bg-gray-200'}`}
                onClick={() => setEditForm((f) => ({ ...f, canReserve: !f.canReserve }))}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${editForm.canReserve ? 'translate-x-5' : 'translate-x-1'}`}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Puede crear reservaciones</p>
                <p className="text-xs text-gray-400">Permite al mesero gestionar reservaciones de mesas</p>
              </div>
            </label>
          )}
          {editError && <p className="text-xs text-red-500">{editError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setEditTarget(null)}>Cancelar</Button>
            <Button
              loading={isUpdating}
              disabled={!editForm.name || !editForm.email}
              onClick={() => update()}
            >
              Guardar cambios
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={!!confirmDelete}
        title={`¿Eliminar a ${confirmDelete?.name}?`}
        message="El empleado perderá acceso al sistema inmediatamente."
        warning="Esta acción no se puede deshacer."
        onConfirm={() => { remove(confirmDelete!.id); setConfirmDelete(null) }}
        onCancel={() => setConfirmDelete(null)}
      />

      <Modal title="Nuevo empleado" open={open} onClose={handleClose}>
        <div className="space-y-4">
          <Field label="Nombre">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Juan García"
            />
          </Field>
          <Field label="Correo electrónico">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="juan@restaurante.com"
            />
          </Field>
          <Field label="Contraseña temporal">
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
            />
          </Field>
          <Field label="Rol">
            <Select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
            >
              <option value={UserRole.OWNER}>Dueño / Todo en uno</option>
              <option value={UserRole.WAITER}>Mesero</option>
              <option value={UserRole.KITCHEN}>Cocina/Bar</option>
              {hasReception && <option value={UserRole.RECEPTION}>Recepción</option>}
              <option value={UserRole.VALET}>Valet</option>
              <option value={UserRole.HOSTESS}>Hostess</option>
            </Select>
          </Field>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
            <Button
              loading={isPending}
              disabled={!form.name || !form.email || !form.password}
              onClick={() => create()}
            >
              Crear empleado
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function StaffRow({
  member,
  onEdit,
  onRemove,
}: {
  member: StaffMember
  onEdit: () => void
  onRemove: () => void
}) {
  return (
    <div className="px-5 py-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-900">{member.name}</p>
        <p className="text-xs text-gray-400">{member.email}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          label={ROLE_LABEL[member.role] ?? member.role}
          color={ROLE_COLOR[member.role] ?? 'gray'}
        />
        {member.role === UserRole.WAITER && member.canReserve && (
          <Badge label="Reserva ✓" color="green" />
        )}
        <Button size="sm" variant="secondary" onClick={onEdit}>
          Editar
        </Button>
        <Button size="sm" variant="danger" onClick={onRemove}>
          Eliminar
        </Button>
      </div>
    </div>
  )
}
